'use client'

import ChevronDown from '@/components/icons/chevron-down'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { Check } from 'lucide-react'
import React, { useState } from 'react'

function MultiFilter({
  value,
  setValue,
  options,
  description,
  displayLabel,
  valueLabel,
  parentClassName,
  displayClassName,
}) {
  const [open, setOpen] = useState(false)

  if (!options) return <Skeleton className={cn('h-full', parentClassName)} />

  const foundOption = options?.find((o) =>
    valueLabel ? o[valueLabel] === value[0] : o === value[0],
  )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn('justify-between capitalize', parentClassName)}
        >
          <p className="truncate">
            {value.length === options?.length
              ? `All ${description}`
              : value.length > 1
                ? `${value.length} ${description}`
                : value.length === 1
                  ? foundOption && (foundOption[displayLabel] || foundOption)
                  : `All ${description}`}
          </p>

          <ChevronDown
            className={cn(
              'size-4 stroke-white transition-transform duration-200',
              open && 'rotate-180',
            )}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn('p-0', displayClassName)}>
        <Command>
          <CommandInput placeholder={`Search ${description.toLowerCase()}`} />
          <CommandList>
            <CommandEmpty>No {description.toLowerCase()} found.</CommandEmpty>
            <CommandGroup>
              {options?.map((o) => (
                <CommandItem
                  key={o[valueLabel] || o}
                  value={o[valueLabel] || o}
                  onSelect={(cv) => {
                    if (value.includes(cv)) {
                      setValue(value.filter((v) => v !== cv))
                    } else {
                      setValue([...value, cv])
                    }
                    setOpen(false)
                  }}
                >
                  <Check
                    className={cn('mr-2 size-4 opacity-0', {
                      'opacity-100': value.includes(o[valueLabel] || o),
                    })}
                  />
                  {o[displayLabel] || o}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

// TODO - @kr3mil Single Filter
/**
 * Reusable filter component, using shadcn
 * @typedef {Object} FilterProps
 * @property {string[]} value - The selected value(s) (useState)
 * @property {function} setValue - The setter function (useState)
 * @property {Object[] || string[]} options - The options to display
 * @property {string} description - The description to display
 * @property {string} [displayLabel] - The label to display, required if options is Object[] (option[displayLabel])
 * @property {string} [valueLabel] - The value to use, required if options is Object[] (option[valueLabel])
 * @property {string} [parentClassName] - The parent class name
 * @property {string} [displayClassName] - The display class name
 * @property {boolean} [multi=false] - If true, allow multiple selections
 *
 * @example
 * // Multi filter for platforms
 * <Filter
 *  value={platformFilter}
 *  setValue={setPlatformFilter}
 *  options={platforms}
 *  description="Platforms"
 *  displayLabel="longname"
 *  valueLabel="name"
 *  parentClassName="w-[157px]"
 *  displayClassName="w-[157px]"
 *  multi
 * />
 */
function Filter({
  value,
  setValue,
  options,
  description,
  displayLabel,
  valueLabel,
  parentClassName,
  displayClassName,
  multi = false,
}) {
  if (multi)
    return MultiFilter({
      value,
      setValue,
      options,
      description,
      displayLabel,
      valueLabel,
      parentClassName,
      displayClassName,
    })

  const [open, setOpen] = useState(false)

  if (!options) return <Skeleton className={cn('h-full', parentClassName)} />

  const foundOption = options?.find((o) =>
    valueLabel ? o[valueLabel] === value : o === value,
  )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn('justify-between capitalize', parentClassName)}
        >
          <p className="truncate">{foundOption[displayLabel] || foundOption}</p>

          <ChevronDown
            className={cn(
              'size-4 stroke-white transition-transform duration-200',
              open && 'rotate-180',
            )}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn('p-0', displayClassName)}>
        <Command>
          <CommandList>
            <CommandGroup>
              {options?.map((o) => (
                <CommandItem
                  key={o[valueLabel] || o}
                  value={o[valueLabel] || o}
                  onSelect={(cv) => {
                    setValue(cv)
                    setOpen(false)
                  }}
                >
                  <Check
                    className={cn('mr-2 size-4 opacity-0', {
                      'opacity-100': value === o[valueLabel] || value === o,
                    })}
                  />
                  {o[displayLabel] || o}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export default React.memo(Filter)
